import { useEffect, useState, type ClipboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Brain, ExternalLink, FileText, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { clipboardToMarkdown, insertAtSelection } from "@/lib/htmlToMarkdown";
import { api, Task, TaskLink, TaskPriority } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

type TaskOwner = "arek" | "kasia";

const PRIORITIES: TaskPriority[] = ["P0", "P1", "P2"];

function isHttpUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function normalizeLinks(rows: { url: string; label: string }[]): TaskLink[] {
  const out: TaskLink[] = [];
  for (const row of rows) {
    const url = row.url.trim();
    if (!url || !isHttpUrl(url)) continue;
    const label = row.label.trim();
    out.push(label ? { url, label } : { url });
  }
  return out;
}

export default function TaskDetailModal({
  task,
  onClose,
}: {
  task: Task;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || "");
  const [deadline, setDeadline] = useState(task.deadline || "");
  const [owner, setOwner] = useState<TaskOwner>(
    task.owner === "kasia" ? "kasia" : "arek",
  );
  const [priority, setPriority] = useState<TaskPriority>(
    task.priority === "P0" || task.priority === "P2" ? task.priority : "P1",
  );
  const [linkRows, setLinkRows] = useState<{ url: string; label: string }[]>(() => {
    const links = task.links || [];
    if (!links.length) return [{ url: "", label: "" }];
    return links.map((l) => ({ url: l.url, label: l.label || "" }));
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);

  const mapsQ = useQuery({
    queryKey: ["mindmaps", "task-link-picker"],
    queryFn: () => api.mindmapsList(),
    staleTime: 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const attachMindmap = (mapId: string, mapTitle: string) => {
    const url = `${window.location.origin}/mapy/${mapId}`;
    setLinkRows((rows) => {
      const withoutEmpty = rows.filter((r) => r.url.trim() || r.label.trim());
      if (withoutEmpty.some((r) => r.url === url || r.url.endsWith(`/mapy/${mapId}`))) {
        return withoutEmpty.length ? withoutEmpty : [{ url: "", label: "" }];
      }
      return [...withoutEmpty, { url, label: `Mapa: ${mapTitle.slice(0, 60)}` }];
    });
  };

  const mSave = useMutation({
    mutationFn: () => {
      if (linkRows.some((r) => r.url.trim() && !isHttpUrl(r.url))) {
        throw new Error("Link musi zaczynać się od http:// lub https://");
      }
      if (!title.trim()) throw new Error("Tytuł nie może być pusty");
      return api.updateTask(task.id, {
        title: title.trim(),
        description: description.trim() || null,
        deadline: deadline.trim() || null,
        owner,
        priority,
        links: normalizeLinks(linkRows),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message || "Nie udało się zapisać"),
  });

  const mDelete = useMutation({
    mutationFn: () => api.deleteTask(task.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      onClose();
    },
    onError: (e: Error) => setErr(e.message || "Nie udało się usunąć"),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-3"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="task-detail-title"
        className="flex max-h-[min(94dvh,720px)] w-full flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-[#f7f8fa] text-slate-900 shadow-xl sm:max-h-[min(88vh,720px)] sm:w-[min(96vw,560px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 sm:py-4 sm:pt-4">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-slate-300 sm:hidden" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 id="task-detail-title" className="text-lg font-semibold text-slate-900">
                Szczegóły zadania
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">Opis, linki, deadline, owner</p>
            </div>
            <Badge
              variant="secondary"
              className={cn(
                "border-0",
                priority === "P0" && "bg-rose-100 text-rose-800",
                priority === "P1" && "bg-amber-100 text-amber-900",
                priority === "P2" && "bg-slate-200 text-slate-700",
              )}
            >
              {priority}
            </Badge>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4 sm:p-5">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Tytuł</span>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-white" />
          </label>

          <label className="block space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Opis</span>
              {description.trim() ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 bg-white"
                  disabled={converting}
                  onClick={async () => {
                    setErr(null);
                    setConverting(true);
                    try {
                      const res = await api.docsFromKanban({
                        task_id: task.id,
                        title: title.trim() || task.title,
                        body_md: description.trim(),
                        panel_origin: window.location.origin,
                      });
                      setDescription(res.task.description || "");
                      const links = res.task.links || [];
                      setLinkRows(
                        links.length
                          ? links.map((l) => ({ url: l.url, label: l.label || "" }))
                          : [{ url: "", label: "" }],
                      );
                      await qc.invalidateQueries({ queryKey: ["tasks"] });
                    } catch (e) {
                      setErr(e instanceof Error ? e.message : "Nie udało się utworzyć dokumentu");
                    } finally {
                      setConverting(false);
                    }
                  }}
                >
                  <FileText className="mr-1 h-3.5 w-3.5" />
                  {converting ? "Tworzę…" : "Zamień na dokument"}
                </Button>
              ) : null}
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={(e: ClipboardEvent<HTMLTextAreaElement>) => {
                const md = clipboardToMarkdown(e.clipboardData);
                if (!md) return;
                e.preventDefault();
                const ta = e.currentTarget;
                const start = ta.selectionStart ?? description.length;
                const end = ta.selectionEnd ?? description.length;
                const { next, caret } = insertAtSelection(description, start, end, md);
                setDescription(next);
                requestAnimationFrame(() => {
                  ta.focus();
                  ta.setSelectionRange(caret, caret);
                });
              }}
              rows={5}
              placeholder="Kontekst, kroki, uwagi… (wklej z Docs/Word → od razu markdown)"
              className="flex min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Deadline
              </span>
              <Input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="bg-white"
              />
            </label>
            <div className="space-y-1.5">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Owner</span>
              <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
                {(["arek", "kasia"] as TaskOwner[]).map((o) => (
                  <Button
                    key={o}
                    type="button"
                    size="sm"
                    variant={owner === o ? "default" : "ghost"}
                    className={cn(
                      "h-8 flex-1",
                      owner === o && o === "arek" && "bg-blue-600 hover:bg-blue-700",
                      owner === o && o === "kasia" && "bg-pink-600 hover:bg-pink-700",
                    )}
                    onClick={() => setOwner(o)}
                  >
                    {o === "arek" ? "Arek" : "Kasia"}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Priorytet
            </span>
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-0.5">
              {PRIORITIES.map((p) => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={priority === p ? "default" : "ghost"}
                  className={cn(
                    "h-8 flex-1",
                    priority === p && p === "P0" && "bg-rose-600 hover:bg-rose-700",
                    priority === p && p === "P1" && "bg-amber-600 hover:bg-amber-700",
                    priority === p && p === "P2" && "bg-slate-600 hover:bg-slate-700",
                  )}
                  onClick={() => setPriority(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Linki</span>
              <div className="flex items-center gap-1">
                <select
                  className="h-7 max-w-[10rem] rounded-md border border-slate-200 bg-white px-1.5 text-[11px] text-slate-700"
                  defaultValue=""
                  onChange={(e) => {
                    const mid = e.target.value;
                    e.target.value = "";
                    if (!mid) return;
                    const item = (mapsQ.data?.items || []).find((m) => m.id === mid);
                    if (item) attachMindmap(item.id, item.title);
                  }}
                  title="Dodaj link do mapy myśli"
                >
                  <option value="">+ mapa myśli…</option>
                  {(mapsQ.data?.items || []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 bg-white"
                  onClick={() => setLinkRows((rows) => [...rows, { url: "", label: "" }])}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Dodaj
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              {linkRows.map((row, idx) => (
                <div
                  key={idx}
                  className="flex flex-col gap-1.5 rounded-md border border-slate-200 bg-white p-2 sm:flex-row sm:items-center"
                >
                  <Input
                    placeholder="https://…"
                    value={row.url}
                    onChange={(e) =>
                      setLinkRows((rows) =>
                        rows.map((r, i) => (i === idx ? { ...r, url: e.target.value } : r)),
                      )
                    }
                    className="bg-white sm:flex-1"
                  />
                  <Input
                    placeholder="Etykieta"
                    value={row.label}
                    onChange={(e) =>
                      setLinkRows((rows) =>
                        rows.map((r, i) => (i === idx ? { ...r, label: e.target.value } : r)),
                      )
                    }
                    className="bg-white sm:w-36"
                  />
                  <div className="flex gap-1">
                    {row.url.trim() && isHttpUrl(row.url) ? (
                      <a
                        href={row.url.trim()}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
                        title="Otwórz link"
                      >
                        {/\/mapy\//i.test(row.url) || /^mapa:/i.test(row.label) ? (
                          <Brain className="h-4 w-4 text-indigo-600" />
                        ) : (
                          <ExternalLink className="h-4 w-4" />
                        )}
                      </a>
                    ) : null}
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-9 w-9 text-slate-500"
                      onClick={() =>
                        setLinkRows((rows) =>
                          rows.length <= 1
                            ? [{ url: "", label: "" }]
                            : rows.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {err ? <p className="text-sm text-destructive">{err}</p> : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5 sm:pb-3">
          <div>
            {!confirmDelete ? (
              <Button
                type="button"
                variant="outline"
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
                onClick={() => setConfirmDelete(true)}
              >
                Usuń
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-rose-700">Na pewno?</span>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={mDelete.isPending}
                  onClick={() => mDelete.mutate()}
                >
                  Tak, usuń
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>
                  Anuluj
                </Button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Zamknij
            </Button>
            <Button
              type="button"
              variant="brand"
              disabled={mSave.isPending}
              onClick={() => {
                setErr(null);
                mSave.mutate();
              }}
            >
              {mSave.isPending ? "Zapis…" : "Zapisz"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
